/**
 * Date / time helpers for the two string formats this app shuttles
 * between in form state vs API payloads:
 *
 *   • UsDateString  — "MM-DD-YYYY" (form / dropdown state, VisitDate.date)
 *   • IsoDateString — "YYYY-MM-DD" (API payloads, ShiftSelected.date)
 *
 * Both are typed as `string` because TS strings are structural — the
 * branded aliases below only document intent at call sites; they do not
 * enforce the format at compile time. Use the helpers below for any
 * format conversion so the rules live in one place.
 */

import moment from 'moment';

/** Date string in MM-DD-YYYY format. */
export type UsDateString = string;

/** Date string in YYYY-MM-DD format. */
export type IsoDateString = string;

/** Time string in HH:MM (24h) or HH:MM:SS. */
export type TimeString = string;

const US_DATE_PATTERN = /^(\d{2})-(\d{2})-(\d{4})$/;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Convert "MM-DD-YYYY" → "YYYY-MM-DD". Returns the input unchanged when
 * the format isn't recognised so callers operating on partially-populated
 * state don't crash.
 */
export const usToIso = (us: UsDateString): IsoDateString => {
    const match = US_DATE_PATTERN.exec(us);
    if (!match) {
        return us;
    }
    const [, month, day, year] = match;
    return `${year}-${month}-${day}`;
};

/**
 * Convert "YYYY-MM-DD" → "MM-DD-YYYY". Mirrors {@link usToIso} for the
 * other direction.
 */
export const isoToUs = (iso: IsoDateString): UsDateString => {
    const match = ISO_DATE_PATTERN.exec(iso);
    if (!match) {
        return iso;
    }
    const [, year, month, day] = match;
    return `${month}-${day}-${year}`;
};

/**
 * A selectedShift-shaped row, as far as date matching is concerned.
 *
 * The list holds two shapes at once, which is what makes this awkward:
 *   • rows a date-selection form created — `day` is "MM-DD-YYYY", `date` is ISO
 *   • rows hydrated from the API on amend — no `day` at all, `date` is ISO
 */
export interface DateMatchableShift {
    day?: string | undefined;
    date?: string | undefined;
    blockDate?: string | null | undefined;
}

/**
 * Does this shift row sit on the given calendar date ("MM-DD-YYYY")?
 *
 * Use this for every "drop / keep the shifts for this date" filter. Matching on
 * `day` alone silently misses every hydrated row (they have no `day`), which
 * left an amended request carrying the shift the user had just replaced — a
 * Night shift swapped for a Custom one was saved as both.
 *
 * `blockDate` is deliberately NOT consulted: a split shift's sibling blocks are
 * removed by their own blockDate filter, and folding it in here would drop
 * unrelated bookings on the day a shift spills onto.
 */
export const shiftIsOnCalendarDate = (
    shift: DateMatchableShift,
    usDate: UsDateString,
): boolean => {
    if (!usDate) {
        return false;
    }
    const iso = usToIso(usDate);
    return shift.day === usDate || shift.date === usDate || shift.date === iso;
};

/**
 * Parse a date string that may be MM-DD-YYYY (form / dropdown state) or
 * YYYY-MM-DD / full ISO (API) into a moment.
 *
 * Use this instead of `moment(value)` or `new Date(value)`: both fall back
 * to the browser's native parser for non-ISO strings, which returns Invalid
 * Date in Safari (but not Chrome) and surfaces as "Invalid date" in the UI.
 */
export const parseUsOrIso = (value: string): moment.Moment => (
    moment(value, ['MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true)
);

/**
 * Build a `Date` from a UsDateString plus a HH:MM(:SS) time. Returns an
 * Invalid Date when either input doesn't match the expected format —
 * callers should guard with `!Number.isNaN(d.getTime())`.
 */
export const parseShiftDateTime = (us: UsDateString, time: TimeString): Date => (
    new Date(`${usToIso(us)}T${time}`)
);

const TIME_PATTERN = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/;

/**
 * Add `hours` (signed integer) to a HH:MM(:SS) time string and return
 * the result formatted as "HH:MM". Pure string arithmetic — does NOT
 * round-trip through `Date`, because `Date.getHours()`/`setHours()`
 * operate in the browser's local timezone. In timezones with a
 * non-zero minute offset (Asia/Kolkata +05:30, Asia/Tehran +03:30,
 * Asia/Kathmandu +05:45, Australia/Adelaide +09:30/+10:30,
 * Canada/Newfoundland -03:30) the local-time hours don't match the
 * UTC hours, and the minute-portion of the offset leaks into the
 * result — e.g. `new Date('1970-01-01T07:00:00Z').getHours()` is
 * `7` in UTC but `12` in IST, and a subsequent `setHours(12 + 11)`
 * preserves the +30 minutes from IST and yields `23:30`. That bug
 * produced the spurious "Open Day 07:00 AM - 11:30 PM" option for
 * users in half-hour zones.
 *
 * Wraps modulo 24 — so adding 13 hours to "20:00" returns "09:00".
 */
export const addHoursToTime = (time: TimeString, hours: number): TimeString => {
    const match = TIME_PATTERN.exec(time);
    if (!match) {
        return time;
    }
    const h = Number(match[1]);
    const m = Number(match[2]);
    if (Number.isNaN(h) || Number.isNaN(m)) {
        return time;
    }
    const dayMinutes = 24 * 60;
    const total = ((h + hours) * 60 + m) % dayMinutes;
    const wrapped = (total + dayMinutes) % dayMinutes;
    const newH = Math.floor(wrapped / 60);
    const newM = wrapped % 60;
    return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
};

/**
 * Format a HH:MM(:SS) time string as a 24-hour "HH:MM" (leading zeros,
 * minutes always shown — e.g. "09:00", "17:30"). This is the single
 * source of truth for time display across the app; prefer it over ad-hoc
 * moment/dayjs/date-fns format calls. Pure string parsing on
 * {@link TIME_PATTERN} — does NOT round-trip through `Date` /
 * `toLocaleTimeString`, which leaks the browser timezone (see the
 * {@link addHoursToTime} note on half-hour zones). Returns the input
 * unchanged when it isn't a recognised HH:MM(:SS) string, matching the
 * other helpers here so partially-populated state doesn't crash. Nullish
 * input (missing start/end times) yields an empty string.
 */
export const formatTime = (time: TimeString | null | undefined): string => {
    if (!time) {
        return '';
    }
    const match = TIME_PATTERN.exec(time);
    if (!match) {
        return time;
    }
    const h = Number(match[1]);
    const m = Number(match[2]);
    if (Number.isNaN(h) || Number.isNaN(m)) {
        return time;
    }
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Format a pair of HH:MM(:SS) times as a 24-hour range "HH:MM - HH:MM".
 */
export const formatTimeRange = (
    start: TimeString | null | undefined,
    end: TimeString | null | undefined,
): string => (
    `${formatTime(start)} - ${formatTime(end)}`
);

/**
 * Format a full datetime (ISO 8601, or "YYYY-MM-DD HH:MM:SS") as a local
 * 24-hour "HH:MM" — e.g. a check-in/out timestamp.
 *
 * Distinct from {@link formatTime}, which takes a bare time string and
 * deliberately avoids `Date` to dodge timezone leakage. Here the value
 * carries a date, so going through `Date` is correct: the result is in the
 * viewer's local timezone, matching the `moment(value).format('HH:mm')`
 * usage this replaces. Returns '' for nullish or unparseable input.
 */
export const formatDateTimeToTime = (value: string | null | undefined): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};
